import { Link } from 'react-router-dom';
import { FiAlertTriangle } from 'react-icons/fi';

import { useBilling } from '../../../hooks/useBilling';
import { useOrganization } from '../../../contexts/OrganizationContext';
import styles from './PaymentStatusBanner.module.css';

/**
 * Persistent notice for an organisation whose subscription is not paid up.
 *
 * Until now a failed renewal was invisible to the customer: `past_due` appeared
 * in the frontend exactly once, as a badge colour on an admin page. The gateway
 * now refuses paid work for an unpaid subscription with a 402, and the existing
 * 402 handlers render that as a generic error toast - which tells someone
 * mid-task that something went wrong, but not that their card was declined or
 * what to do about it.
 *
 * Deliberately not dismissible. The condition is not advisory and it does not
 * resolve on its own; it resolves when the customer updates their payment
 * method. A dismiss button would only hide the one thing they need to act on.
 */
export default function PaymentStatusBanner() {
  // useBilling requires the organisation to read billing for; it self-guards on
  // a falsy id, so the banner simply stays hidden until the org context resolves.
  // Safe here because PaymentStatusBanner mounts inside OrganizationProvider
  // (App.tsx wraps RouterProvider), and useOrganization throws outside it.
  const { currentOrganization } = useOrganization();
  const { billing } = useBilling(currentOrganization?.id);

  const status = billing?.subscription?.status;
  if (status !== 'past_due' && status !== 'unpaid') {
    return null;
  }

  // `unpaid` is Stripe's terminal dunning state; `past_due` means retries are
  // still scheduled. The customer's action is identical, but the urgency and
  // the current consequence are not, so say which one they are in.
  const isTerminal = status === 'unpaid';

  return (
    <div className={styles.banner} role="alert" aria-live="polite">
      <FiAlertTriangle className={styles.icon} aria-hidden="true" />
      <div className={styles.body}>
        <strong className={styles.title}>
          {isTerminal
            ? 'Your subscription is unpaid'
            : 'Your last payment did not go through'}
        </strong>
        <span className={styles.detail}>
          {isTerminal
            ? 'Paid features are suspended until the outstanding invoice is settled.'
            : 'We will retry automatically, but paid features will be suspended if the payment keeps failing.'}
        </span>
      </div>
      <Link to="/admin/billing" className={styles.action}>
        Update payment method
      </Link>
    </div>
  );
}
